/**
 * Sentry can only initialize once the runtime `/v2/api/client-config` fetch in
 * main.ts resolves with a DSN. Nothing is listening for errors before that:
 * not while `./legacy` eagerly `require.context`-loads the entire AngularJS
 * app, not while `@b2b-ui` evaluates, and not while that fetch is in flight.
 * A throw during any of that is exactly the class of boot failure error
 * monitoring exists to catch, and would otherwise be lost entirely.
 *
 * This module buffers `error`/`unhandledrejection` events from the moment
 * it's imported until `flushEarlyErrors` hands them to a reporter (Sentry, or
 * a console fallback if Sentry never got a DSN). It must be the first
 * non-CSS import in main.ts so its listeners attach before any riskier import
 * gets a chance to throw.
 */

const buffered: unknown[] = [];
const MAX_BUFFERED = 25;

function record(error: unknown): void {
  if (buffered.length < MAX_BUFFERED) buffered.push(error);
}

function handleError(event: ErrorEvent): void {
  record(event.error ?? event.message);
}

function handleRejection(event: PromiseRejectionEvent): void {
  record(event.reason);
}

window.addEventListener('error', handleError);
window.addEventListener('unhandledrejection', handleRejection);

/**
 * Stops buffering and hands every error captured so far to `report`. Safe to
 * call at most once per page load; the listeners are removed here because
 * once Sentry is initialized its own global handlers take over.
 */
export function flushEarlyErrors(report: (error: unknown) => void): void {
  window.removeEventListener('error', handleError);
  window.removeEventListener('unhandledrejection', handleRejection);

  const toFlush = buffered.splice(0, buffered.length);
  toFlush.forEach((error) => {
    try {
      report(error);
    } catch {
      // Reporting itself must never break boot.
    }
  });
}
